import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Announcement } from '../models/announcement';
import { environment } from 'src/environments/environment';
import { AnnouncementResponse } from '../models/announcement-response';
import { AnnouncementSubscriptionRequest } from '../models/announcement-subscription-request';
import { AnnouncementSubscription } from '../models/AnnouncementSubscription';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root',
})
export class AnnouncementsService {
  private readonly cachePrefix = 'announcements';
  private readonly cacheDuration = 48 * 60 * 60 * 1000; // 48 hours in milliseconds
  private readonly jsonHeaders = new HttpHeaders().set('Content-Type', 'application/json');

  constructor(
    private readonly http: HttpClient,
    private readonly cacheService: CacheService
  ) {}

  getAnnouncements(
    page: number,
    pageSize: number,
    useCache: boolean = true
  ): Observable<AnnouncementResponse> {
    const params = `pageSize=${pageSize}&pageNumber=${page}`;
    const url = `${environment.announcementsapi.announcementsUri}?${params}`;
    const cacheKey = this.cacheService.generateCacheKey(this.cachePrefix, ['list', params]);

    return this.cacheService.getCachedOrFetch<AnnouncementResponse>(
      cacheKey,
      this.cacheDuration,
      () => this.http.get<AnnouncementResponse>(url, {
        observe: 'body',
        headers: this.jsonHeaders
      }),
      useCache
    );
  }

  deleteAnnouncement(announcement: Announcement): Observable<any> {
    // Clear cache when modifying data
    this.cacheService.clearByPrefix(this.cachePrefix);

    return this.http.delete(
      `${environment.announcementsapi.announcementsUri}/${announcement.announcementId}`,
      {
        observe: 'body',
        headers: this.jsonHeaders
      }
    );
  }

  createOrUpdateAnnouncement(result: Announcement): Observable<any> {
    // Clear cache when modifying data
    this.cacheService.clearByPrefix(this.cachePrefix);

    return this.http.post(
      `${environment.announcementsapi.announcementsUri}`,
      result,
      {
        observe: 'body',
        headers: this.jsonHeaders
      }
    );
  }

  getSubscriptions(useCache: boolean = true): Observable<AnnouncementSubscription[]> {
    const url = `${environment.announcementsapi.subscriptionsUri}`;
    const cacheKey = this.cacheService.generateCacheKey(this.cachePrefix, ['subscriptions']);

    return this.cacheService.getCachedOrFetch<AnnouncementSubscription[]>(
      cacheKey,
      this.cacheDuration,
      () => this.http.get<AnnouncementSubscription[]>(url, {
        observe: 'body',
        headers: this.jsonHeaders
      }),
      useCache
    );
  }

  deleteSubscription(email: string): Observable<any> {
    // Clear subscriptions cache when modifying data
    this.cacheService.clearByPrefix(`${this.cachePrefix}_subscriptions`);

    return this.http.delete(
      `${environment.announcementsapi.subscribeUri}/${email}`,
      {
        observe: 'response'
      }
    );
  }

  subscribeToAnnouncements(
    result: AnnouncementSubscriptionRequest
  ): Observable<any> {
    // Clear subscriptions cache when modifying data
    this.cacheService.clearByPrefix(`${this.cachePrefix}_subscriptions`);

    return this.http.post(
      `${environment.announcementsapi.subscribeUri}`,
      result,
      {
        observe: 'response',
        headers: this.jsonHeaders
      }
    );
  }
}
